/**
 * Registration form
 */

import React, { useState } from 'react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { useAuth } from '../../hooks/useAuth';
import { Database, Mail, User, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import { TraceKitConfig } from '../../types/config';

interface RegisterFormProps {
  onSuccess?: () => void;
  config?: TraceKitConfig;
}

export function RegisterForm({ onSuccess, config }: RegisterFormProps) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register, isValidating } = useAuth();
  const { addToast } = useToastStore();

  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const passwordErrors = password ? validatePassword(password) : [];
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordErrors.length > 0) {
      addToast({
        message: 'Please fix password requirements',
        type: 'error',
      });
      return;
    }

    if (!passwordsMatch) {
      addToast({
        message: 'Passwords do not match',
        type: 'error',
      });
      return;
    }

    try {
      const response = await register({
        email: email.trim(),
        username: username.trim(),
        password,
        full_name: fullName.trim() || undefined,
      });

      addToast({
        message: response.message || 'Registration successful! Please check your email to verify your account.',
        type: 'success',
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error handling is done in useAuth hook
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
      
      <div className="w-full max-w-md space-y-8 animate-slide-in">
        {/* Logo and header */}
        <div className="text-center">
          <div className="inline-flex p-4 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-2xl shadow-blue-500/50 mb-6">
            <Database className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent">
            Create Account
          </h1>
          <p className="mt-3 text-lg text-slate-600 font-medium">
            Join TraceKit Platform
          </p>
        </div>

        {/* Registration card */}
        <div className="bg-white/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-slate-200/50">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Full Name (Optional)
              </label>
              <Input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Doe"
                leftIcon={<User className="h-5 w-5 text-slate-400" />}
                className="h-12 text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Email *
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                leftIcon={<Mail className="h-5 w-5 text-slate-400" />}
                className="h-12 text-base"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Username *
              </label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="johndoe"
                leftIcon={<User className="h-5 w-5 text-slate-400" />}
                className="h-12 text-base"
                autoComplete="username"
                required
              />
              <p className="mt-1 text-xs text-slate-500">3-100 characters, letters, numbers, underscores, and hyphens only</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  leftIcon={<Lock className="h-5 w-5 text-slate-400" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                  }
                  className="h-12 text-base pr-12"
                  autoComplete="new-password"
                  required
                />
              </div>
              {password && (
                <div className="mt-2 space-y-1">
                  {[
                    { check: password.length >= 12, text: 'At least 12 characters' },
                    { check: /[A-Z]/.test(password), text: 'One uppercase letter' },
                    { check: /[a-z]/.test(password), text: 'One lowercase letter' },
                    { check: /[0-9]/.test(password), text: 'One number' },
                    { check: /[!@#$%^&*(),.?":{}|<>]/.test(password), text: 'One special character' },
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      {req.check ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-slate-300" />
                      )}
                      <span className={req.check ? 'text-green-600' : 'text-slate-500'}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Confirm Password *
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  leftIcon={<Lock className="h-5 w-5 text-slate-400" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                  }
                  className="h-12 text-base pr-12"
                  autoComplete="new-password"
                  required
                />
              </div>
              {confirmPassword && (
                <div className="mt-2">
                  {passwordsMatch ? (
                    <div className="flex items-center gap-2 text-xs text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      Passwords match
                    </div>
                  ) : (
                    <div className="text-xs text-red-600">
                      Passwords do not match
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={
                !email.trim() || 
                !username.trim() || 
                !password || 
                !confirmPassword ||
                passwordErrors.length > 0 ||
                !passwordsMatch ||
                isValidating
              }
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all transform hover:scale-[1.02]"
            >
              {isValidating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Creating account...
                </>
              ) : (
                <>
                  <User className="h-5 w-5" />
                  Create Account
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-xs text-center text-slate-500">
              Already have an account?{' '}
              <a
                href="#"
                className="text-blue-600 hover:text-blue-700 font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  if (onSuccess) onSuccess();
                }}
              >
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

